import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { PermissionBit } from "../../../api";
import { PPGQuickLook } from "../../../models/quick-look-analyses";
import { MedData, MedDataItem } from "./med-data";

export type MergedMedDataItem = MedDataItem & {
  data_source?: MedDataSource;
  tags?: string[];
  ppgConditions?: string[];
  ppgDevice?: string | string[];
  ppgQuickLook?: PPGQuickLook | null;
  override_value?: number | null;
  seerlinq_measurement_quality_flag?: number;
};

export class MergedMedData extends MedData<MergedMedDataItem> {
  private quickLookByRowKey = new Map<string, PPGQuickLook>();

  constructor(
    data: MergedMedDataItem[],
    permissions?: PermissionBit[],
    userUUID?: string,
  ) {
    super(data, permissions, userUUID);
  }

  originFieldName: string = "data_source";
  flagKey: string = "seerlinq_measurement_quality_flag";
  tagsKey: string = "tags";
  condsKey: string = "ppgConditions";
  deviceKey: string = "ppgDevice";
  quickLookKey: string = "ppgQuickLook";

  medDataKeys: string[] = [
    "measurement_datetime",
    "measurement_type",
    "measurement_value",
    "measurement_unit",
    "comment",
    "created_at",
    "update_history",
    "uuid",
    this.flagKey,
    this.tagsKey,
    this.deviceKey,
    this.condsKey,
    this.quickLookKey,
    "override_value",
  ];

  updateEndpoint = {
    [MedDataSource.PatientOrPhysician]: "medicaldata",
    [MedDataSource.SQAlgo]: "computed",
    [MedDataSource.PPGDerived]: "derived",
  };

  mergeWithPPGDerivedAndComputed(
    derivedDataList: any[],
    computedDataList: any[],
    keyToRename: string,
    valueMapping: Record<string, string>,
  ) {
    const activeQuickLookRowKeys = new Set<string>();

    const filterAndLabel = (
      list: any[],
      label: MedDataSource,
      renameKey: boolean = true,
      defaultQF: number | null = null,
      defaultTag: string[] | null = null,
      defaultDevice: string | null = null,
      defaultCond: string[] | null = null,
    ) =>
      list.map((item) => {
        const filteredItem = this.medDataKeys.reduce<any>((acc, key) => {
          if (item.hasOwnProperty(key)) {
            acc[key] =
              renameKey &&
              key === keyToRename &&
              valueMapping[item[key]] !== undefined
                ? valueMapping[item[key]]
                : item[key];
          }
          return acc;
        }, {});
        filteredItem[this.originFieldName] = label;
        if (defaultQF !== null) {
          filteredItem[this.flagKey] = defaultQF;
        }
        if (defaultTag !== null) {
          filteredItem[this.tagsKey] = defaultTag;
        }
        if (defaultDevice !== null) {
          filteredItem[this.deviceKey] = defaultDevice;
        }
        if (defaultCond !== null) {
          filteredItem[this.condsKey] = defaultCond;
        }
        const nextQuickLook = filteredItem[this.quickLookKey] as
          | PPGQuickLook
          | undefined;

        if (nextQuickLook) {
          // Reuse the existing QuickLook instance so loaded/loading state survives refetches.
          const rowKey = `${label}:${filteredItem.uuid}`;
          const previousQuickLook = this.quickLookByRowKey.get(rowKey);
          const quickLookToUse = previousQuickLook ?? nextQuickLook;

          quickLookToUse.syncSelection(
            nextQuickLook.analysisType,
            nextQuickLook.uuids,
          );
          filteredItem[this.quickLookKey] = quickLookToUse;
          this.quickLookByRowKey.set(rowKey, quickLookToUse);
          activeQuickLookRowKeys.add(rowKey);
        }

        return filteredItem;
      });
    const medData = filterAndLabel(
      this.data,
      MedDataSource.PatientOrPhysician,
      false,
      -1,
      ["Not tagged"],
      "--",
      ["--"],
    );
    const derivedData = derivedDataList
      .map((item) =>
        filterAndLabel(item.data, MedDataSource.PPGDerived, true, null, [
          "Not tagged",
        ]),
      )
      .flat();
    const computedData = computedDataList
      .map((item) => filterAndLabel(item.data, MedDataSource.SQAlgo, true))
      .flat();
    this.data = [...medData, ...derivedData, ...computedData];

    for (const rowKey of this.quickLookByRowKey.keys()) {
      if (!activeQuickLookRowKeys.has(rowKey)) {
        this.quickLookByRowKey.delete(rowKey);
      }
    }
  }

  override validateItemDelete(item: any): string | undefined {
    if (item.data_source !== MedDataSource.PatientOrPhysician) {
      return i18n._(msg`Cannot delete`);
    }

    return super.validateItemDelete(item);
  }
}

export enum MedDataSource {
  PatientOrPhysician = "Patient / Physician",
  SQAlgo = "SQ-algo",
  PPGDerived = "PPG-derived",
}
